// 🛰️ Using centralized RecurrenceRangeType to ensure system-wide enum compatibility
import { PriorityTier, TaskScope, KanbanTask, RecurrenceRangeType } from '@/types';

/**
 * 🧠 ADVANCED MAINTENANCE INTENT PARSER
 * Transforms natural language into structured Asset Ledger data.
 * v1.7: Fixed regex lookaheads and strict type adherence.
 */

export interface ParsedMaintenanceIntent {
  title: string;
  description?: string;
  tags?: string[];
  scope?: TaskScope;
  recurrence?: {
    isEnabled: boolean;
    interval: number;
    frequency: RecurrenceRangeType | null;
    completionTrigger?: boolean;
    lastResetDate?: string;
  };
  dueDate?: string;
  priorityTier?: PriorityTier;
  cost?: number;
  consumption?: KanbanTask['consumption'];
  isHistorical?: boolean;
  provider?: string;
}

// ... existing imports ...

/**
 * parseMaintenanceInput
 * Uses heuristic regex patterns to extract entities from unstructured text.
 */
export const parseMaintenanceInput = (input: string): ParsedMaintenanceIntent => {
  const text = input.toLowerCase().trim();
  const now = new Date();
  
  // 1. 📅 TEMPORAL CONTEXT (Historical vs. Future)
  const pastMarkers = ['last', 'ago', 'yesterday', 'fixed', 'changed', 'replaced', 'installed', 'did'];
  const isHistorical = pastMarkers.some(marker => text.includes(marker));

  // 2. 🔁 RECURRENCE LOGIC (e.g., "Every 6 months")
  const intervalMatch = text.match(/every (\d+)/);
  const parsedInterval = intervalMatch ? parseInt(intervalMatch[1]) : 1; // Renamed to avoid clash

  let parsedFrequency: RecurrenceRangeType | null = null; // Renamed
  if (text.includes('every') || text.includes('each')) {
    if (text.includes('year') || text.includes('yr')) parsedFrequency = 'years';
    else if (text.includes('week')) parsedFrequency = 'weeks';
    else if (text.includes('day')) parsedFrequency = 'days';
    else parsedFrequency = 'months'; 
  }

  // 3. 💰 FINANCIAL EXTRACTION (e.g., "$1,200" or "1200 dollars")
  const costMatch = text.match(/\$?(\d{1,3}(?:,?\d{3})*(?:\.\d{2})?)/);
  const parsedCost = costMatch ? parseFloat(costMatch[1].replace(/,/g, '')) : undefined; // Changed to undefined for no match

  // 4. 👷 PROVIDER DETECTION (e.g., "by Apex Plumbing")
  const providerMatch = text.match(/by\s+([a-z0-9\s]+?)(?=\s+(?:every|last|for|at|on|in|\$|\d)|$)/i);
  const parsedProvider = providerMatch ? providerMatch[1].trim() : undefined; // Renamed and changed to undefined for no match

  // 5. 🏷️ TITLE CLEANING
  let title = input
    .replace(/every \d+ (day|week|month|year)s?/gi, '')
    .replace(/every (day|week|month|year)s?/gi, '')
    .replace(/last (week|month|year|june|may|october|monday|tuesday|yesterday)/gi, '')
    .replace(/by\s+([a-z0-9\s]+?)(?=\s+(?:every|last|for|at|on|in|\$|\d)|$)/i, '')
    .replace(/\$?(\d{1,3}(?:,?\d{3})*(?:\.\d{2})?)/, '')
    .replace(/(fixed|changed|replaced|installed|remind me to|i )/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  title = title ? title.charAt(0).toUpperCase() + title.slice(1) : 'New Maintenance Log';

  // --- CONSTRUCT THE ParsedMaintenanceIntent OBJECT ---
  const recurrence: ParsedMaintenanceIntent['recurrence'] = (parsedFrequency && parsedInterval) ? {
    isEnabled: true,
    interval: parsedInterval,
    frequency: parsedFrequency,
    completionTrigger: false, // Defaulting as it's not parsed from NLP
    lastResetDate: undefined, // Will be set by useTaskState or logic based on completion
  } : undefined;

  const dueDate = isHistorical ? now.toISOString() : undefined; // Map isHistorical to dueDate

  return {
    title,
    description: undefined, // Not parsed from NLP, default to undefined
    tags: undefined,        // Not parsed from NLP, default to undefined
    scope: 'household',     // Default scope, or parsed if your NLP supports it
    recurrence,
    dueDate,
    priorityTier: undefined, // Not parsed from NLP, default to undefined
    cost: parsedCost,       // Use the parsed cost
    consumption: undefined,  // Not parsed from NLP, default to undefined
    // Note: 'provider' is parsed but not included in ParsedMaintenanceIntent,
    // so it's omitted here. If needed, add it to the interface.
  };
};